// React imports
import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { useOutletContext, useNavigate, useParams } from 'react-router-dom';

// Generic imports
import _ from 'lodash';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import pluralize from 'pluralize';
import axios from 'axios';
import prettyBytes from 'pretty-bytes';
import { v4 as uuidv4 } from 'uuid';

// MUI imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import Tooltip from '@mui/material/Tooltip';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import Backdrop from '@mui/material/Backdrop';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Avatar from '@mui/material/Avatar';
import { useTheme } from '@mui/material/styles';
import { grey, red, yellow } from '@mui/material/colors';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEnvelopeDot, faFile, faFilePdf, faFileWord, faFileExcel, faFilePowerpoint, faFileLines, faFileCsv, 
    faFileAudio, faFileVideo, faFileZipper, faFileCode, faFileImage, faCircle, faSearch, faStar
} from '@fortawesome/pro-solid-svg-icons';
import { faPaperclip, faStar as faStarLight, faReply, faThumbtack } from '@fortawesome/pro-light-svg-icons';

// Local imports
import Header from '../../Header';
import email from '../utils/email';
import { statusToText } from '../../OpportunityRow.utils.js';
import EmailConversation from './EmailPage.conversation';
import AttachmentWarning from './EmailPage.attachmentwarning';
import { getOpportunitiesFromServer } from '../utils/opportunity';
import { Divider } from '@mui/material';

dayjs.extend(advancedFormat);

function EmailPage(props) {
    const { org, accesstoken, opportunities, emailLoginInfo, onEmailRefreshConnection, 
        onUpdateOpportunity, me, exchangeRates, lockedExchangeRates, accounts, suppliers, 
        seaPorts, unlocode, setAccounts, removedThreads, setRemovedThreads, drawerWidth, notifications, setNotifications } = useOutletContext();
    const { emailId } = useParams();
    const [ snackbar, setSnackbar ] = useState(null);
    const [ selectedFolder, setSelectedFolder ] = useState(null);
    const [ conversations, setConversations ] = useState(null);
    const [ search, setSearch ] = useState(null);
    const [ loading, setLoading ] = useState(null);
    const [ opportunityStatusFilter, setOpportunityStatusFilter ] = useState('');
    const prevSearchRef = useRef(null);

    // Helper function to check if conversations have changed
    function hasConversationsChanged(oldConversations, newConversations) {
        if (!oldConversations || !newConversations) return true;
        if (!oldConversations.conversations || !newConversations.conversations) return true;
        
        const oldMap = new Map();
        oldConversations.conversations.forEach(conv => {
            if (conv && conv[0]) {
                oldMap.set(conv[0].conversationId, conv);
            }
        });

        const newMap = new Map();
        newConversations.conversations.forEach(conv => {
            if (conv && conv[0]) {
                newMap.set(conv[0].conversationId, conv);
            }
        });

        // Check for new conversations
        for (const [conversationId, newConv] of newMap) {
            if (!oldMap.has(conversationId)) {
                return true; // New conversation found
            }
        }

        // Check for updated conversations
        for (const [conversationId, newConv] of newMap) {
            const oldConv = oldMap.get(conversationId);
            if (!oldConv) continue;

            // Create maps of messages by ID for comparison
            const oldMessageMap = new Map(oldConv.map(msg => [msg.id, msg]));
            const newMessageMap = new Map(newConv.map(msg => [msg.id, msg]));

            // Check for new messages
            for (const [messageId] of newMessageMap) {
                if (!oldMessageMap.has(messageId)) {
                    return true; // New message found
                }
            }

            // Check for updated messages (flags, read status, etc.)
            for (const [messageId, newMsg] of newMessageMap) {
                const oldMsg = oldMessageMap.get(messageId);
                if (!oldMsg) continue;

                // Compare key properties that might change
                if (oldMsg.flagged !== newMsg.flagged ||
                    oldMsg.unread !== newMsg.unread ||
                    oldMsg.receivedDateTime !== newMsg.receivedDateTime) {
                    return true; // Message updated
                }
            }
        }

        return false; // No changes detected
    }

    const fetchConversations = useCallback(async (force = false) => {
        if (!emailLoginInfo || !selectedFolder || !emailLoginInfo.tokens) return;
        try {
            console.log('[EmailPage] fetchConversations called', { force, folder: selectedFolder });

            // Use cached version for faster initial load
            const _conversations = await email.getConversationsWithCache({
                org,
                tokens: emailLoginInfo.tokens,
                folder: selectedFolder,
                search,
                encryptionKey: me?.cacheKey,
                onCacheHit: (cachedData) => {
                    // Show cached data immediately while fetching fresh data
                    if (!force && cachedData) {
                        console.log('[EmailPage] Showing cached data while fetching fresh');
                        setConversations(cachedData);
                        if (loading) {
                            setLoading(false); // Hide loading spinner when cache is shown
                        }
                    }
                }
            });
            console.log('[EmailPage] fetchConversations - received', _conversations?.conversations?.length, 'conversations', _conversations);

            // If conversations is null (email not enabled) or empty array (expired token handled), don't update
            if (!_conversations) {
                console.log('[EmailPage] No conversations returned (email not enabled)');
                return;
            }

            // Check if search has changed
            const searchChanged = prevSearchRef.current !== search;
            prevSearchRef.current = search;

            notifyOnRateRequestEmails({ 
                conversations: _conversations?.conversations, 
                opportunities, 
                notifications, 
                myEmail: emailLoginInfo.email, 
                org, 
                tokens: emailLoginInfo.tokens, 
                accesstoken 
            });
            
            // When search changes or force refresh, always set conversations to the returned results
            // Otherwise, only update if there are changes (for polling)
            setConversations(prev => {
                if (searchChanged || force) {
                    // Search changed or forced refresh - always set to new results
                    console.log('[EmailPage] fetchConversations - forcing update', { searchChanged, force });
                    return _conversations;
                }
                // Search unchanged - only update if there are changes (for polling)
                const hasChanges = hasConversationsChanged(prev, _conversations);
                if (hasChanges) {
                    console.log('[EmailPage] fetchConversations - changes detected, updating');
                    return _conversations;
                }
                console.log('[EmailPage] fetchConversations - no changes detected, keeping previous');
                return prev;
            });
        } catch (e) {
            console.log('[EmailPage] error with getConversations', e);
            // Check if this is an expired token error
            const errorMessage = e?.message || e?.error?.message || JSON.stringify(e);
            const isExpiredError = errorMessage?.includes('Lifetime validation failed') || 
                                  errorMessage?.includes('expired') ||
                                  errorMessage?.includes('InvalidAuthenticationToken') ||
                                  e?.statusCode === 401 ||
                                  e?.code === 'InvalidAuthenticationToken';
            
            if (isExpiredError) {
                console.log('[EmailPage] Expired token detected, showing error message');
                setSnackbar({
                    open: true,
                    message: 'Your email connection has expired. Please reconnect your email account.',
                    severity: 'error',
                    action: (
                        <Button color="inherit" size="small" onClick={() => {
                            if (onEmailRefreshConnection) {
                                onEmailRefreshConnection();
                            }
                        }}>
                            Reconnect
                        </Button>
                    )
                });
            }
        }
    }, [ emailLoginInfo, selectedFolder, search, org, me ]);

    useEffect(() => {
        if (!emailLoginInfo || !selectedFolder || !emailLoginInfo.tokens) return;
        (async () => {
            try {
                console.log('[EmailPage] useEffect', emailLoginInfo, selectedFolder);

                const encryptionKey = me?.cacheKey;

                // Try to load from cache immediately (before showing loading spinner)
                if (!search && encryptionKey) {
                    try {
                        const cached = await email.getCachedConversations({
                            encryptionKey,
                            orgId: org.id,
                            folder: selectedFolder
                        });
                        if (cached && cached.data) {
                            console.log('[EmailPage] Loading from cache immediately');
                            setConversations(cached.data);
                            if (cached.isStale) {
                                setLoading(true);
                            }
                        } else {
                            setLoading(true);
                        }
                    } catch (e) {
                        console.error('[EmailPage] Error reading cache:', e);
                        setLoading(true);
                    }
                } else {
                    setLoading(true);
                }

                await fetchConversations();
                setLoading(false);
            } catch (e) {
                console.log('[EmailPage] error with getConversations', e);
                setLoading(false);
            }
        })();
    }, [ emailLoginInfo?.status, selectedFolder, search ]);

    // Polling effect - check for new emails every 30 seconds (only when not searching)
    useEffect(() => {
        if (!emailLoginInfo || !selectedFolder || !emailLoginInfo.tokens || search) return;
        
        const intervalId = setInterval(() => {
            fetchConversations();
        }, 30000); // 30 seconds

        return () => clearInterval(intervalId);
    }, [ emailLoginInfo?.status, selectedFolder, search, fetchConversations ]);

    async function getMoreConversations() {
        const _conversations = await email.getConversations({ 
            org, 
            tokens: emailLoginInfo.tokens, 
            folder: selectedFolder, 
            search, 
            nextPageToken: conversations.nextPageToken 
        });
        setConversations(prev => ({ ...prev, conversations: [ ...prev.conversations, ..._conversations.conversations ], nextPageToken: _conversations.nextPageToken }));
    }

    if (!emailLoginInfo || !opportunities) return (
        <Box>
            <Header
                icon={faEnvelopeDot}
                title={'Your Mailbox'}
            />
            <Box sx={{ p: 3, flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                <CircularProgress />
            </Box>
        </Box>
    )

    if (emailLoginInfo.status === 'not-connected' || emailLoginInfo.status === 'expired') return (
        <Box>
            <Header
                icon={faEnvelopeDot}
                title={'Your Mailbox'}
            />
            <Box sx={{ p: 3, flexGrow: 1 }}>
                <EmailPaneConfiguration
                    org={org}
                    accesstoken={accesstoken}
                    onEmailDone={onEmailRefreshConnection}
                />
            </Box>
        </Box>
    )

    return (
        <Box>
            <Header
                icon={faEnvelopeDot}
                title={'Your Mailbox'}
                noMarginBottom={true}
            />
            <Box sx={{ flexGrow: 1 }}>
                <EmailMessages
                    tokens={emailLoginInfo?.tokens}
                    accesstoken={accesstoken}
                    org={org}
                    opportunities={opportunities}
                    me={me}
                    onUpdateOpportunity={onUpdateOpportunity}
                    setSnackbar={setSnackbar}
                    emailLoginInfo={emailLoginInfo}
                    emailConversations={conversations?.conversations || []}
                    getMoreConversations={!!conversations?.nextPageToken ? getMoreConversations : null}
                    exchangeRates={exchangeRates}
                    lockedExchangeRates={lockedExchangeRates}
                    accounts={accounts}
                    setAccounts={setAccounts}
                    suppliers={suppliers}
                    seaPorts={seaPorts}
                    unlocode={unlocode}
                    emailId={emailId}
                    removedThreads={removedThreads}
                    setRemovedThreads={setRemovedThreads}
                    selectedFolder={selectedFolder}
                    setSelectedFolder={(e) => {
                        setSearch('');
                        setOpportunityStatusFilter('');
                        setSelectedFolder(e);
                    }}
                    drawerWidth={drawerWidth}
                    onSearch={setSearch}
                    activeSearch={search}
                    opportunityStatusFilter={opportunityStatusFilter}
                    setOpportunityStatusFilter={setOpportunityStatusFilter}
                    refreshConversations={fetchConversations}
                />
            </Box>
            <Snackbar
                open={!!snackbar}
                autoHideDuration={snackbar?.severity === 'error' ? 6000 : 3000}
                onClose={() => setSnackbar(null)}
                action={snackbar?.action}
            >
                {snackbar?.severity ? (
                    <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity} sx={{ width: '100%' }}>
                        {snackbar?.message}
                    </Alert>
                ) : (
                    snackbar?.message
                )}
            </Snackbar>
            {loading && (
                <Box sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999 }}>
                    <Box sx={{ height: '3px', background: 'linear-gradient(90deg, #3b82f6, #8b5cf6, #3b82f6)', backgroundSize: '200% 100%', animation: 'shimmer 1.5s infinite', '@keyframes shimmer': { '0%': { backgroundPosition: '200% 0' }, '100%': { backgroundPosition: '-200% 0' } } }} />
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 0.5, background: 'rgba(255,255,255,0.95)', borderBottom: '1px solid #e2e8f0' }}>
                        <CircularProgress size={14} sx={{ mr: 1 }} />
                        <Typography sx={{ fontSize: '11px', color: '#64748b' }}>Loading mailbox...</Typography>
                    </Box>
                </Box>
            )}
        </Box>
    )
}

function Directory(props) {
    const { org, emailLoginInfo, selected, setSelected, encryptionKey } = props;
    const [ folders, setFolders ] = useState([]);
    const [ labels, setLabels ] = useState([]);

    useEffect(() => {
        // Load from cache immediately (async)
        (async () => {
            if (encryptionKey) {
                try {
                    const cached = await email.getCachedFolders({
                        encryptionKey,
                        orgId: org.id
                    });
                    if (cached) {
                        console.log('[Directory] Loading folders from cache');
                        setFolders(cached.folders || []);
                        setLabels(cached.labels || []);
                        if (cached.defaultFolderId && !selected) {
                            setSelected(cached.defaultFolderId);
                        }
                    }
                } catch (e) {
                    console.error('[Directory] Error reading folders cache:', e);
                }
            }

            // Fetch fresh data
            try {
                const _folders = await email.getFolders({ org, tokens: emailLoginInfo.tokens });
                const defaultFolderId = await email.getDefaultFolderId({ org, folders: _folders, tokens: emailLoginInfo.tokens });
                const sortedFolders = [
                    ..._folders.filter(f => f.id === defaultFolderId),
                    ..._.sortBy(_folders.filter(f => f.id !== defaultFolderId), a => a.name),
                ]
                const _labels = await email.getLabels({ org, tokens: emailLoginInfo.tokens });

                // Update state with fresh data
                setFolders(sortedFolders);
                setLabels(_labels);
                if (!selected) {
                    setSelected(defaultFolderId);
                }

                // Cache the fresh data
                if (encryptionKey) {
                    email.setCachedFolders({
                        encryptionKey,
                        orgId: org.id,
                        folders: sortedFolders,
                        labels: _labels,
                        defaultFolderId
                    }).catch(e => console.error('[Directory] Error writing folders cache:', e));
                }
            } catch (e) {
                console.log('[Directory] error with getFolders', e);
            }
        })();
    }, [ emailLoginInfo?.status, encryptionKey ]);

    return (
        <Box sx={{ width: '95px', minWidth: '95px', height: 'calc(100vh - 64px)', overflowY: 'auto', borderRight: '1px solid rgba(0,0,0,0.12)' }}>
            <List sx={{ padding: '0px !important' }} dense>
                {!_.isEmpty(folders) && (
                    <Typography variant='caption' sx={{ p: 1, opacity: 0.5, fontSize: '11px', lineHeight: 1 }}>Folders</Typography>
                )}
                {folders.map(folder => (
                    <ListItem 
                        key={folder.id} 
                        sx={{ 
                            background: selected === folder.id ? 'rgba(0,0,0,0.05)' : 'transparent', 
                            padding: 0,
                            minHeight: 'auto'
                        }} 
                        onClick={() => setSelected(folder.id)} 
                        button
                    >
                        <ListItemText 
                            primary={(
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '11px', lineHeight: 1, paddingLeft: '8px' }}>
                                    {folder.color && (
                                        <Box sx={{ width: '6px', height: '6px', background: folder.color.backgroundColor, borderRadius: '50%' }} />
                                    )}
                                    <Typography sx={{ fontSize: '11px', lineHeight: 1 }}>{folder.name}</Typography>
                                </Box>
                            )}
                            sx={{ '& .MuiListItemText-primary': { fontSize: '11px', lineHeight: 1 } }}
                        />
                    </ListItem>
                ))}
                {!_.isEmpty(labels) && (
                    <>
                        <Divider sx={{ margin: '6px', width: 'calc(100% - 12px)', marginLeft: '6px' }} />
                        <Typography variant='caption' sx={{ p: 1, opacity: 0.5, fontSize: '11px', lineHeight: 1 }}>Labels</Typography>
                    </>
                )}
                {labels.map(label => (
                    <ListItem 
                        key={label.id} 
                        sx={{ 
                            background: selected === label.id ? 'rgba(0,0,0,0.05)' : 'transparent', 
                            padding: 0,
                            minHeight: 'auto'
                        }} 
                        onClick={() => setSelected(label.id)} 
                        button
                    >
                        <ListItemText 
                            primary={(
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, fontSize: '11px', lineHeight: 1, paddingLeft: '8px' }}>
                                    {label.color && (
                                        <Box sx={{ width: '6px', height: '6px', background: label.color.backgroundColor, borderRadius: '50%' }} />
                                    )}
                                    <Typography sx={{ fontSize: '11px', lineHeight: 1 }}>{label.name}</Typography>
                                </Box>
                            )}
                            sx={{ '& .MuiListItemText-primary': { fontSize: '11px', lineHeight: 1 } }}
                        />
                    </ListItem>
                ))}
            </List>
        </Box>
    )

}

function Layout(props) {
    // Create a layout that has a top bar with some elements (all under the prop "topbar"), a sidebar (all under the prop "sidebar"), and a main content area (all under the prop "content")
    const { topbar, sidebar, content, contentOpen, org, emailLoginInfo, selectedFolder, setSelectedFolder, drawerWidth, encryptionKey } = props;

    // Calculate the difference between full sidebar width (240px) and current drawer width
    // When minimized (60px), this gives us 180px to add to the email list
    const sidebarWidthDifference = 240 - drawerWidth;
    const emailListWidth = contentOpen ? (280 + sidebarWidthDifference) : '100%';

    return (
        <Box sx={{ display: 'flex', flexDirection: 'row' }}>
            <Directory org={org} emailLoginInfo={emailLoginInfo} selected={selectedFolder} setSelected={setSelectedFolder} encryptionKey={encryptionKey} />

            <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)', maxWidth: `calc(100vw - 95px - ${drawerWidth}px - 30px)`, flex: 1 }}>
                <Box sx={{ flexShrink: 0 }}>
                    {topbar}
                </Box>
                <Box sx={{ display: 'flex', flexGrow: 1, overflow: 'hidden' }}>
                    <Box sx={{ width: emailListWidth, flexShrink: 0, borderRight: contentOpen ? '1px solid rgba(0,0,0,0.12)' : 'none', overflow: 'auto' }}>
                        {sidebar}
                    </Box>
                    {contentOpen && (
                        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 3 }}>
                            {content}
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    )
}

function formatTimestamp(dateString) {
    const date = dayjs(dateString);
    const now = dayjs();
    const daysDiff = now.diff(date, 'day');
    
    if (date.isSame(now, 'day')) {
        // Today: show just the time (e.g., "11:45 AM")
        return date.format('h:mm A');
    } else if (daysDiff === 1) {
        // Yesterday: show "Yesterday" + time (e.g., "Yesterday 4:20 PM")
        return `Yesterday ${date.format('h:mm A')}`;
    } else if (daysDiff <= 7) {
        // This week (Sun–Fri): show day name + time (e.g., "Mon 9:00 AM")
        return `${date.format('ddd')} ${date.format('h:mm A')}`;
    } else {
        // Older than 1 week: show date with full month name (e.g., "January 5th 2026")
        return date.format('MMMM Do YYYY');
    }
}

function stringAvatar(name) {
    if (!name) return { sx: { bgcolor: '#eee' }, children: '?' };
    const [ first, last ] = name?.split(' ');
    return {
        sx: {
            bgcolor: stringToColor(name),
        },
        children: `${first[0]}${last ? last[0] : first[1] || first[0]}`.toUpperCase(),
    };
}

function stringToColor(string) {
    let hash = 0;
    let i;

    /* eslint-disable no-bitwise */
    for (i = 0; i < string.length; i += 1) {
        hash = string.charCodeAt(i) + ((hash << 5) - hash);
    }

    let color = '#';

    for (i = 0; i < 3; i += 1) {
        const value = (hash >> (i * 8)) & 0xff;
        color += `00${value.toString(16)}`.slice(-2);
    }
    /* eslint-enable no-bitwise */

    return color;
}

function getPreheaderSnippet(conversation) {
    // Get the latest message by timestamp (not by array position, since order depends on messageOrder)
    const latestMessage = _.maxBy(conversation, m => new Date(m.receivedDateTime).getTime());
    if (latestMessage?.snippet) {
        return latestMessage.snippet.trim();
    }
    
    // Fallback: extract from body content if available
    if (latestMessage?.body?.content) {
        const text = latestMessage.body.content
            .replace(/<style([\s\S]*?)<\/style>/gi, '')
            .replace(/<script([\s\S]*?)<\/script>/gi, '')
            .replace(/<[^>]+>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();
        return text.substring(0, 100); // Limit to reasonable length
    }
    
    return null;
}

function highlightSearchTerm(text, searchTerm) {
    if (!text || !searchTerm) return text;
    
    // Escape special regex characters in search term
    const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    
    // Split text by search term (case-insensitive), keeping the matches
    const parts = text.split(new RegExp(`(${escapedTerm})`, 'gi'));
    
    return parts.map((part, index) => {
        // Check if this part matches the search term (case-insensitive)
        if (part.toLowerCase() === searchTerm.toLowerCase()) {
            return (
                <span key={index} style={{ backgroundColor: '#ffeb3b', fontWeight: 500 }}>
                    {part}
                </span>
            );
        }
        return part;
    });
}

function EmailMessages(props) {
    const { tokens, accesstoken, org, opportunities, me, onUpdateOpportunity, setSnackbar, emailLoginInfo, emailId,
        exchangeRates, lockedExchangeRates, accounts, suppliers, seaPorts, unlocode, setAccounts, emailConversations, 
        removedThreads, setRemovedThreads, selectedFolder, setSelectedFolder, drawerWidth, onSearch, getMoreConversations,
        activeSearch, opportunityStatusFilter, setOpportunityStatusFilter, refreshConversations } = props;

    const theme = useTheme();
    const [ notes, setNotes ] = useState([]);
    const [ openConversation, setOpenConversation ] = useState(null);
    const [ showAttachmentWarning, setShowAttachmentWarning ] = useState(false);
    const [ mergeNewMessages, setMergeNewMessages ] = useState([]);
    const [ read, setRead ] = useState([]);
    const [ loadingMore, setLoadingMore ] = useState(false);
    const [ search, setSearch ] = useState('');
    const [ searching, setSearching ] = useState(false);
    const [ searchFocused, setSearchFocused ] = useState(false);
    
    // Determine message order based on email supplier
    const messageOrder = useMemo(() => email.getEmailThreadOrder(org), [org]);
    
    // Create a stable key that changes when opportunity statuses change to force filter recalculation
    const opportunitiesStatusKey = useMemo(() => {
        if (!opportunities || opportunities.length === 0) return '';
        // Create a hash based on opportunity IDs and their statuses
        return opportunities
            .filter(o => o.source?.type === 'email' && o.source?.id)
            .map(o => `${o.id}:${o.status || 'no-status'}`)
            .sort()
            .join('|');
    }, [opportunities]);

    useEffect(() => {
        setOpenConversation(emailId || null);
    }, [ emailId ]);

    const navigate = useNavigate();

    useEffect(() => {
        // TODO: Get only on new conversations
        setMergeNewMessages([]);
        // Search completed when emailConversations updates
        if (searching) {
            setSearching(false);
        }
    }, [ emailConversations ]);

    // Reset searching state when search is cleared externally
    useEffect(() => {
        if (!search && searching) {
            setSearching(false);
        }
    }, [ search ]);

    const allConversations = useMemo(() => {
        const conversations = _.cloneDeep(emailConversations);
        for (const message of mergeNewMessages) {
            const conversation = conversations.find(c => c[0].conversationId === message.conversationId);
            if (conversation) {
                if (conversation.find(m => m.id === message.id)) {
                    // Replace inplace
                    conversation.splice(0, conversation.length, ...conversation.map(m => m.id === message.id ? message : m));
                } else {
                    conversation.push(message);
                }
            } else {
                conversations.push([ message ]);
            }
        }
        // Sort messages within each conversation based on user preference
        const sortMultiplier = messageOrder === 'desc' ? -1 : 1;
        conversations.map(c => c.sort((a, b) => sortMultiplier * (new Date(a.receivedDateTime).getTime() - new Date(b.receivedDateTime).getTime())));
        
        // Sort conversations by the latest received (non-user) message
        // so that our own replies don't push conversations to the top
        const userEmail = emailLoginInfo?.email;
        conversations.sort((a, b) => {
            const aMessages = userEmail ? a.filter(m => m.sender?.emailAddress?.address !== userEmail) : a;
            const bMessages = userEmail ? b.filter(m => m.sender?.emailAddress?.address !== userEmail) : b;
            const aLatest = (aMessages.length > 0 ? aMessages : a).reduce((latest, msg) => {
                const msgTime = new Date(msg.receivedDateTime).getTime();
                return msgTime > latest ? msgTime : latest;
            }, 0);
            const bLatest = (bMessages.length > 0 ? bMessages : b).reduce((latest, msg) => {
                const msgTime = new Date(msg.receivedDateTime).getTime();
                return msgTime > latest ? msgTime : latest;
            }, 0);
            return bLatest - aLatest; // Sort descending (newest first)
        });
        
        let filtered = conversations.filter(c => !removedThreads.includes(c[0].conversationId));
        
        // Filter by opportunity status
        if (opportunityStatusFilter) {
            filtered = filtered.filter(conversation => {
                const conversationOpportunities = opportunities?.filter(o => 
                    o.source?.type === 'email' && 
                    o.source?.id && 
                    !!conversation.find(c => c?.conversationId === o.source?.id)
                ) || [];
                
                if (_.isEmpty(conversationOpportunities)) {
                    return false;
                }
                
                // Match the exact logic from ConversationOpportunityStatus display
                const openOpportunity = conversationOpportunities.find(o => !o.status || o.status === 'draft' || o.status === 'pre-draft');
                
                // Check for draft status - only show if there's an open/draft opportunity (which would display as "Draft")
                if (opportunityStatusFilter === 'draft') {
                    return !!openOpportunity;
                }
                
                // For other statuses, if there's an open/draft opportunity, it should only show when filtering for draft
                if (openOpportunity) {
                    return false;
                }
                
                // Sort by created date (most recent first) - create a copy to avoid mutating
                const sortedOpportunities = [...conversationOpportunities].sort((a, b) => b.created - a.created);
                const opportunity = sortedOpportunities[0];
                
                // Use statusToText to match the display logic exactly
                const displayedStatus = statusToText({ opportunity });
                
                // Check for specific status - match against what would be displayed
                if (opportunityStatusFilter === 'sent-to-endcustomer') {
                    return displayedStatus === 'Sent to Customer';
                }
                if (opportunityStatusFilter === 'opened-by-customer') {
                    return displayedStatus === 'Opened by Customer';
                }
                if (opportunityStatusFilter === 'endcustomer-accepted') {
                    return displayedStatus === 'Accepted by Customer';
                }
                if (opportunityStatusFilter === 'endcustomer-rejected') {
                    return displayedStatus === 'Rejected by Customer';
                }
                if (opportunityStatusFilter === 'completed') {
                    return displayedStatus === 'Won';
                }
                if (opportunityStatusFilter === 'lost') {
                    return displayedStatus === 'Lost';
                }
                return false;
            });
        }
        
        // Re-sort filtered conversations by the latest received (non-user) message
        filtered.sort((a, b) => {
            const aMessages = userEmail ? a.filter(m => m.sender?.emailAddress?.address !== userEmail) : a;
            const bMessages = userEmail ? b.filter(m => m.sender?.emailAddress?.address !== userEmail) : b;
            const aLatest = (aMessages.length > 0 ? aMessages : a).reduce((latest, msg) => {
                const msgTime = new Date(msg.receivedDateTime).getTime();
                return msgTime > latest ? msgTime : latest;
            }, 0);
            const bLatest = (bMessages.length > 0 ? bMessages : b).reduce((latest, msg) => {
                const msgTime = new Date(msg.receivedDateTime).getTime();
                return msgTime > latest ? msgTime : latest;
            }, 0);
            return bLatest - aLatest; // Sort descending (newest first)
        });
        
        return filtered;
    }, [ emailConversations, removedThreads, mergeNewMessages, opportunityStatusFilter, opportunities, opportunitiesStatusKey, messageOrder, emailLoginInfo?.email ]);

    async function onCreateOpportunity({ conversationId, attachments = [], force }) {
        const conversation = allConversations.find(c => c[0].conversationId === conversationId);

        const allOpportunities = await getOpportunitiesFromServer({ accesstoken, organizationId: org.id, me, onProgress: () => {} });
        const existingOpportunity = _.find(allOpportunities, o => { 
            if (!o?.source?.globalMessageId) return false;
            if (o?.source?.globalMessageId === 'undefined') return false;
            if (!conversation[0].globalMessageId) return false;
            if (conversation[0].globalMessageId === 'undefined') return false;
            return decodeURIComponent(o.source.globalMessageId) === decodeURIComponent(conversation[0].globalMessageId);
        });
        if (existingOpportunity) {
            throw { message: 'exists', id: existingOpportunity.id };
        }

        // Skip attachment warning popup - proceed directly
        // if (!force) {
        //     if (_.some(conversation, msg => msg.hasAttachments)) {
        //         setShowAttachmentWarning(conversationId);
        //         return;
        //     }
        // }

        const id = uuidv4();
        const defaultViewTemplate = org?.defaults?.viewTemplate || 'standard';
        onUpdateOpportunity({ opportunity: { 
            status: 'draft', 
            id, 
            type: 'one-off-email', 
            account: {}, 
            offers: [], 
            products: [], 
            terms: [], 
            created: new Date().getTime(), 
            createdBy: me.email, 
            owner: me.email, 
            source: { type: 'email', id: conversationId, globalMessageId: conversation[0].globalMessageId || '' },
            routes: [],
            description: {},
            ...(defaultViewTemplate !== 'standard' ? { attributes: { viewTemplate: defaultViewTemplate } } : {}),
            updates: [{
                created: new Date().getTime(),
                createdBy: me.email,
                files: JSON.parse(JSON.stringify(attachments)),
                html: `
                    <div style="font-size: 11px;">
                        <p>We'll automatically fill out the opportunity information based on the email you provided.</p>
                    </div>`,
            }]
        }});

        navigate(`/organization/${org.alias}/opportunities/${id}/`);
    }

    function onMergeNewMessages({ messages }) {
        setMergeNewMessages(prev => [ ...prev, ...messages ]);
    }

    function onSetRead(messageIds) {
        setRead(prev => [ ...prev, ...messageIds ]);
    }

    function onSetUnread(messageIds) {
        setRead(prev => prev.filter(id => !messageIds.includes(id)));
    }

    return (
        <>
            <Layout
                org={org}
                emailLoginInfo={emailLoginInfo}
                selectedFolder={selectedFolder}
                setSelectedFolder={(e) => {
                    setSearch('');
                    setOpportunityStatusFilter('');
                    if (emailId) {
                        setOpenConversation(null);
                        navigate(`/organization/${org.alias}/email`);
                    }
                    setSelectedFolder(e);
                }}
                drawerWidth={drawerWidth}
                encryptionKey={me?.cacheKey}
                topbar={(
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1, alignItems: 'center', gap: 3, borderBottom: '1px solid rgba(0,0,0,0.12)' }}>
                        <Box sx={{ flex: 1 }}>
                            <TextField
                                variant='outlined'
                                placeholder='Search mail'
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                                onFocus={() => setSearchFocused(true)}
                                onBlur={() => setSearchFocused(false)}
                                onKeyPress={e => {
                                    if (e.key === 'Enter') {
                                        if (search === activeSearch) return;
                                        setSearching(true);
                                        onSearch(search);
                                    }
                                }}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position='start'>
                                            <FontAwesomeIcon 
                                                icon={faSearch} 
                                                style={{ 
                                                    fontSize: '14px',
                                                    color: searchFocused ? theme.palette.primary.main : theme.palette.text.secondary,
                                                    transition: 'color 0.2s ease'
                                                }} 
                                            />
                                        </InputAdornment>
                                    ),
                                    endAdornment: searching ? (
                                        <InputAdornment position='end'>
                                            <CircularProgress size={18} />
                                        </InputAdornment>
                                    ) : null,
                                }}
                                sx={{ 
                                    width: '100%',
                                    '& .MuiOutlinedInput-root': {
                                        height: '36px',
                                        fontSize: '13px',
                                        backgroundColor: searchFocused ? 'white' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.04)'),
                                        transition: 'all 0.2s ease',
                                        boxShadow: searchFocused ? '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)' : 'none',
                                        '&:hover': {
                                            backgroundColor: searchFocused ? 'white' : (theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'),
                                            boxShadow: searchFocused ? '0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)' : '0 1px 2px rgba(0,0,0,0.08)',
                                        },
                                        '&.Mui-focused': {
                                            backgroundColor: 'white',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.15), 0 1px 3px rgba(0,0,0,0.25)',
                                        },
                                        '& fieldset': {
                                            borderColor: searchFocused ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.23)',
                                            transition: 'border-color 0.2s ease',
                                        },
                                        '&:hover fieldset': {
                                            borderColor: searchFocused ? theme.palette.primary.main : 'rgba(0, 0, 0, 0.4)',
                                        },
                                        '&.Mui-focused fieldset': {
                                            borderColor: theme.palette.primary.main,
                                            borderWidth: '2px',
                                        },
                                        '& input': {
                                            padding: '8px 12px',
                                        },
                                        '& input::placeholder': {
                                            opacity: searchFocused ? 0.7 : 0.6,
                                            transition: 'opacity 0.2s ease',
                                        }
                                    }
                                }}
                            />
                        </Box>
                        <FormControl sx={{ minWidth: 200 }}>
                            <Select
                                value={opportunityStatusFilter || ''}
                                onChange={e => setOpportunityStatusFilter(e.target.value)}
                                displayEmpty
                                renderValue={(value) => {
                                    if (!value || value === '') return 'All';
                                    // Find the label for the selected value
                                    const labels = {
                                        'draft': 'Draft',
                                        'sent-to-endcustomer': 'Sent to Customer',
                                        'opened-by-customer': 'Opened by Customer',
                                        'endcustomer-accepted': 'Accepted by Customer',
                                        'endcustomer-rejected': 'Rejected by Customer',
                                        'completed': 'Won',
                                        'lost': 'Lost'
                                    };
                                    return labels[value] || value;
                                }}
                                sx={{
                                    height: '36px',
                                    fontSize: '12px',
                                    '& .MuiSelect-select': {
                                        padding: '8px 12px',
                                    }
                                }}
                                MenuProps={{
                                    PaperProps: {
                                        sx: {
                                            '& .MuiMenuItem-root': {
                                                fontSize: '13px',
                                                paddingTop: '6px',
                                                paddingBottom: '6px',
                                            }
                                        }
                                    }
                                }}
                            >
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="draft">Draft</MenuItem>
                                <MenuItem value="sent-to-endcustomer">Sent to Customer</MenuItem>
                                <MenuItem value="opened-by-customer">Opened by Customer</MenuItem>
                                <MenuItem value="endcustomer-accepted">Accepted by Customer</MenuItem>
                                <MenuItem value="endcustomer-rejected">Rejected by Customer</MenuItem>
                                <MenuItem value="completed">Won</MenuItem>
                                <MenuItem value="lost">Lost</MenuItem>
                            </Select>
                        </FormControl>
                        <Typography sx={{ fontSize: '11px', textAlign: 'right' }}>
                            {allConversations.length > 0 ?  `${allConversations.length} ${pluralize('conversation', allConversations.length)} found` : 'No conversations found'}
                        </Typography>
                    </Box>
                )}
                sidebar={
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'space-between', pb: 2 }}>
                        {activeSearch && (
                            <Box sx={{ width: '100%', px: 2, pt: 2, pb: 1 }}>
                                <Typography sx={{ fontSize: '14px', fontWeight: 500, color: theme.palette.text.primary }}>
                                    Results
                                </Typography>
                            </Box>
                        )}
                        {allConversations.map(conversation => {
                            const isUnread = _.some(conversation, c => c.unread && !read.includes(c.id));
                            const firstMessage = conversation[0];
                            const from = firstMessage?.sender;
                            const isFlagged = conversation.find(c => c.flagged);
                            // Check for attachments - handle both hasAttachments flag and attachments array
                            const hasAttachments = _.some(conversation, c => {
                                // First check the hasAttachments flag
                                if (c.hasAttachments === true) return true;
                                // Then check if attachments array exists and has non-inline attachments
                                if (c.attachments && Array.isArray(c.attachments)) {
                                    const nonInlineAttachments = c.attachments.filter(att => att && !att.isInline);
                                    if (nonInlineAttachments.length > 0) return true;
                                }
                                // Fallback: Check Gmail message origin payload directly (for metadata format)
                                if (c.origin?.payload?.parts) {
                                    const checkPart = (p) => {
                                        if (p.body?.attachmentId) return true;
                                        if (p.filename && p.mimeType && !p.mimeType.startsWith('text/') && !p.mimeType.startsWith('multipart/')) {
                                            return true;
                                        }
                                        if (p.parts && Array.isArray(p.parts)) {
                                            return p.parts.some(checkPart);
                                        }
                                        return false;
                                    };
                                    if (c.origin.payload.parts.some(checkPart)) return true;
                                }
                                return false;
                            });
                            
                            // Debug: Log conversation data to console (remove after debugging)
                            if (conversation[0]?.subject?.toLowerCase().includes('attachment') || conversation[0]?.subject?.toLowerCase().includes('testing')) {
                                console.log('[EmailPage] Conversation data:', {
                                    conversationId: conversation[0]?.conversationId,
                                    subject: conversation[0]?.subject,
                                    messages: conversation.map(m => ({
                                        id: m.id,
                                        hasAttachments: m.hasAttachments,
                                        attachments: m.attachments,
                                        attachmentsLength: m.attachments?.length,
                                        attachmentsArray: m.attachments
                                    })),
                                    calculatedHasAttachments: hasAttachments
                                });
                            }

                            // Collect all unique senders in the conversation
                            const userEmail = emailLoginInfo?.email;
                            const senderDisplay = (() => {
                                const seen = new Set();
                                const names = [];
                                for (const msg of conversation) {
                                    const s = msg.sender;
                                    const addr = s?.emailAddress?.address;
                                    if (!addr || seen.has(addr)) continue;
                                    seen.add(addr);
                                    if (userEmail && addr === userEmail) {
                                        names.push('Me');
                                    } else {
                                        let name = s?.name;
                                        if (!name) {
                                            const emailParts = addr.split('@')[0];
                                            name = emailParts.replace(/[._-]/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || addr;
                                        }
                                        names.push(name);
                                    }
                                }
                                return names.length > 0 ? names.join(', ') : (from?.name || from?.emailAddress?.address || 'Unknown');
                            })();
                            const replyCount = conversation.length - 1; // Subtract 1 for the original message
                            const preheader = getPreheaderSnippet(conversation);
                            // Get the latest message not sent by the user
                            const nonUserMessages = userEmail
                                ? conversation.filter(m => m.sender?.emailAddress?.address !== userEmail)
                                : conversation;
                            const latestMessage = nonUserMessages.length > 0
                                ? _.maxBy(nonUserMessages, m => new Date(m.receivedDateTime).getTime())
                                : _.maxBy(conversation, m => new Date(m.receivedDateTime).getTime());
                            const timestamp = formatTimestamp(latestMessage?.receivedDateTime);
                            
                            return (
                                <MenuItem
                                    key={conversation[0].conversationId}
                                    sx={{ 
                                        p: 2, 
                                        flexDirection: 'row', 
                                        gap: 2, 
                                        width: '100%', 
                                        borderBottom: '1px solid rgba(0,0,0,0.12)', 
                                        backgroundColor: openConversation === conversation[0].conversationId ? '#f0f0f0' : 'white',
                                        position: 'relative',
                                        borderLeft: isUnread ? '3px solid #2185D0' : '3px solid transparent',
                                        '& .flag-icon': {
                                            opacity: isFlagged ? 1 : 0,
                                            transition: 'opacity 0.2s'
                                        },
                                        '&:hover .flag-icon': {
                                            opacity: 1
                                        }
                                    }}
                                    onClick={() => {
                                        navigate(`/organization/${org.alias}/email/${conversation[0].conversationId}`);
                                    }}
                                >
                                    {/* Avatar */}
                                    <Box sx={{ flexShrink: 0 }}>
                                        <Avatar {...stringAvatar(from?.name || from?.emailAddress?.address || 'Unknown')} sx={{ width: 40, height: 40, fontSize: '14px' }} />
                                    </Box>
                                    
                                    <Box sx={{ flexDirection: 'column', gap: 0, flex: 1, overflow: 'hidden', minWidth: 0, display: 'flex' }}>
                                        {/* Line 1: Sender */}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                                            <Typography 
                                                sx={{ 
                                                    fontSize: '12px', 
                                                    color: '#000000',
                                                    fontWeight: isUnread ? 600 : 'normal',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    flex: 1,
                                                    minWidth: 0,
                                                    display: 'block'
                                                }}
                                                component="div"
                                            >
                                                {activeSearch && senderDisplay
                                                    ? highlightSearchTerm(senderDisplay, activeSearch)
                                                    : (senderDisplay || 'Unknown Sender')
                                                }
                                            </Typography>
                                        </Box>
                                        
                                        {/* Line 2: Subject */}
                                        <Typography 
                                            sx={{ 
                                                fontSize: '12px', 
                                                color: isUnread ? '#2185D0' : '#000000',
                                                fontWeight: isUnread ? 600 : 'normal',
                                                whiteSpace: 'nowrap',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                width: '100%'
                                            }}
                                            component="div"
                                        >
                                            {activeSearch && conversation[0].subject 
                                                ? highlightSearchTerm(conversation[0].subject, activeSearch)
                                                : (conversation[0].subject || <i>No subject</i>)
                                            }
                                        </Typography>
                                        
                                        {/* Line 3: Preheader */}
                                        {preheader && (
                                            <Typography 
                                                sx={{ 
                                                    fontSize: '11px', 
                                                    color: '#242424',
                                                    whiteSpace: 'nowrap',
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    width: '100%'
                                                }}
                                                component="div"
                                            >
                                                {activeSearch 
                                                    ? highlightSearchTerm(preheader, activeSearch)
                                                    : preheader
                                                }
                                            </Typography>
                                        )}
                                    </Box>
                                    {/* Right side: Pin, Flag, Date, and Opportunity Status - aligned to entire row */}
                                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', justifyContent: 'flex-start', gap: 0.5, flexShrink: 0 }}>
                                        {/* Pin icon above timestamp */}
                                        {hasAttachments && (
                                            <Tooltip title="Has attachments">
                                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#64748B' }}>
                                                    <FontAwesomeIcon icon={faThumbtack} style={{ fontSize: '12px' }} />
                                                </Box>
                                            </Tooltip>
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <IconButton 
                                                className="flag-icon"
                                                sx={{ 
                                                    p: 0, 
                                                    m: 0, 
                                                    flexShrink: 0
                                                }} 
                                                onClick={async e => {
                                                    e.stopPropagation();
                                                    e.preventDefault();

                                                    const flagged = conversation.find(c => c.flagged);

                                                    try {
                                                        setMergeNewMessages(prev => [
                                                            ...prev,
                                                            ...conversation.map(c => ({ ...c, flagged: !flagged }))
                                                        ]);
                                                        await email.setConversationFlag({
                                                            org,
                                                            tokens,
                                                            conversationId: conversation[0].conversationId,
                                                            flagged: !flagged
                                                        });
                                                    } catch (e) {
                                                        setMergeNewMessages(prev => [
                                                            ...prev,
                                                            ...conversation.map(c => ({ ...c, flagged }))
                                                        ]);
                                                    }
                                                }}
                                            >
                                                {isFlagged && (
                                                    <FontAwesomeIcon style={{ fontSize: '12px', color: email.getFlagIcon({ org }).color }} icon={email.getFlagIcon({ org }).filled} />
                                                )}
                                                {!isFlagged && (
                                                    <FontAwesomeIcon style={{ fontSize: '12px', color: grey[500] }} icon={email.getFlagIcon({ org }).empty} />
                                                )}
                                            </IconButton>
                                            {conversation.length > 1 && (
                                                <FontAwesomeIcon icon={faReply} style={{ fontSize: '12px', color: grey[500] }} />
                                            )}
                                        </Box>
                                        <Typography sx={{ fontSize: '11px', color: '#242424', flexShrink: 0 }}>
                                            {timestamp}
                                        </Typography>
                                        <ConversationOpportunityStatus conversation={conversation} opportunities={opportunities} />
                                    </Box>
                                </MenuItem>
                            );
                        })}
                        {getMoreConversations && (
                            <Button
                                variant='contained'
                                sx={{ margin: '12px 24px 0px 24px', width: 'calc(100% - 48px)' }}
                                disabled={loadingMore}
                                onClick={async () => {
                                    setLoadingMore(true);
                                    await getMoreConversations();
                                    setLoadingMore(false);
                                }}
                            >
                                {loadingMore ? <CircularProgress size={18} /> : 'Load more'}
                            </Button>
                        )}
                    </Box>
                }
                content={
                    <>
                        {(() => {
                            if (!openConversation) return null;
                            const conversation = allConversations.find(c => c[0].conversationId === openConversation)
                            if (!conversation || !conversation[0]) return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', justifyContent: 'center', alignItems: 'center', gap: 1.5, p: 4, color: '#94a3b8' }}>
                                    <CircularProgress size={20} sx={{ color: '#94a3b8' }} />
                                    <Typography sx={{ fontSize: '12px' }}>Loading conversation...</Typography>
                                </Box>
                            )
                            const globalMessageIds = _(conversation).map(c => c.globalMessageId || c.conversationId).uniq().value();
                            const _notes = _(notes).filter(n => globalMessageIds.includes(n.conversationId)).uniqBy('id').value();

                            return (
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <EmailConversation
                                        key={conversation[0].conversationId}
                                        conversation={conversation}
                                        padding={0}
                                        width='100%'
                                        organization={org}
                                        tokens={tokens}
                                        accesstoken={accesstoken}
                                        onClose={() => {
                                            setOpenConversation(null);
                                            navigate(`/organization/${org.alias}/email`);
                                        }}
                                        onRemove={({ message }) => {
                                            setOpenConversation(null);
                                            setRemovedThreads(prev => [ ...prev, conversation[0].conversationId ]);
                                            if (message) {
                                                setSnackbar({ open: true, message, severity: 'success' });
                                            }
                                            let nextConversation = allConversations.findIndex(c => c[0].conversationId === conversation[0].conversationId) + 1;
                                            while (nextConversation < allConversations.length && removedThreads.includes(allConversations[nextConversation][0].conversationId)) {
                                                nextConversation++;
                                            }
                                            if (nextConversation === allConversations.length) {
                                                nextConversation--;
                                                while (nextConversation >= 0 && (allConversations[nextConversation][0].conversationId === conversation[0].conversationId || removedThreads.includes(allConversations[nextConversation][0].conversationId))) {
                                                    nextConversation--;
                                                }
                                                if (nextConversation === -1) {
                                                    setOpenConversation(null);
                                                    navigate(`/organization/${org.alias}/email`);
                                                }
                                            }
                                            setOpenConversation(allConversations[nextConversation][0].conversationId);
                                            navigate(`/organization/${org.alias}/email/${allConversations[nextConversation][0].conversationId}`);
                                        }}
                                        notes={_notes}
                                        setNotes={setNotes}
                                        emailLoginInfo={emailLoginInfo}
                                        exchangeRates={exchangeRates}
                                        lockedExchangeRates={lockedExchangeRates}
                                        me={me}
                                        accounts={accounts}
                                        suppliers={suppliers}
                                        seaPorts={seaPorts}
                                        unlocode={unlocode}
                                        onUpdateOpportunity={onUpdateOpportunity}
                                        setAccounts={setAccounts}
                                        opportunities={opportunities?.filter(o => o.source?.type === 'email' && o.source?.id && !!conversation.find(c => c?.conversationId === o.source?.id))}
                                        allOpportunities={opportunities}
                                        onMergeNewMessages={onMergeNewMessages}
                                        read={read}
                                        onSetRead={onSetRead}
                                        onSetUnread={onSetUnread}
                                        messageOrder={messageOrder}
                                        refreshConversations={refreshConversations}
                                    />
                                </Box>
                            );
                        })()}
                    </>
                }
                contentOpen={!!openConversation}
            />
            <AttachmentWarning
                open={!!showAttachmentWarning}
                conversationId={showAttachmentWarning}
                conversations={allConversations}
                onClose={() => setShowAttachmentWarning(null)}
                onCreateOpportunity={onCreateOpportunity}
                org={org}
                tokens={tokens}
                accesstoken={accesstoken}
            />
        </>
    )
}

function EmailPaneConfiguration(props) {
    const { org, accesstoken, onEmailDone } = props;

    return (
        <Paper sx={{ p: 3 }}>
            <Typography variant='h6'>
                Lets get started...
            </Typography>
            <Typography variant='body1' sx={{ mt: 2 }}>
                Monada can find pricing opportunities in your emails and automatically add them to your opportunities.
            </Typography>
            <Typography variant='body1' sx={{ mb: 2 }}>
                To get started, click the button below.
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <email.LoginButton
                    org={org}
                    accesstoken={accesstoken}
                    onDone={onEmailDone}
                />
            </Box>
        </Paper>
    );
}

function ConversationOpportunityStatus(props) {
    const { conversation, opportunities } = props;

    if (!opportunities || _.isEmpty(opportunities)) {
        return null;
    }

    const conversationOpportunities = opportunities?.filter(o => o.source?.type === 'email' && o.source?.id && !!conversation.find(c => c?.conversationId === o.source?.id));

    if (_.isEmpty(conversationOpportunities)) {
        return null;
    }

    const chipSx = {
        fontSize: '12px',
        height: 'auto',
        paddingTop: '4px',
        paddingBottom: '4px',
        '& .MuiChip-label': {
            fontSize: '12px',
            padding: '0 8px',
        }
    };

    const openOpportunity = conversationOpportunities?.find(o => !o.status || o.status === 'draft' || o.status === 'pre-draft');

    if (openOpportunity) {
        return <Chip size='small' label='Draft' color='chip-request-new' sx={chipSx} />;
    }

    const opportunity = conversationOpportunities?.sort((a, b) => b.created - a.created)[0];

    return (
        <>
            {!opportunity.status && opportunity.source?.type !== 'email' && (
                <Chip label='Draft' color='chip-request-new' sx={chipSx} />
            )}
            {!opportunity.status && opportunity.source?.type === 'email' && (
                <Chip label='Draft from Mailbox' color='chip-request-new' sx={chipSx} />
            )}
            {statusToText({ opportunity }) === 'Sent to Customer' && (
                <Chip label='Sent' color='chip-sent-to-endcustomer' sx={chipSx} />
            )}
            {statusToText({ opportunity }) === 'Opened by Customer' && (
                <Chip label='Opened' color='chip-opened-by-customer' sx={chipSx} />
            )}
            {statusToText({ opportunity }) === 'Accepted by Customer' && (
                <Chip label='Accepted' color='chip-accepted' sx={chipSx} />
            )}
            {statusToText({ opportunity }) === 'Won' && (
                <Chip label='Won' color='chip-won' sx={chipSx} />
            )}
            {statusToText({ opportunity }) === 'Rejected by Customer' && (
                <Chip label='Rejected' color='chip-lost' sx={chipSx} />
            )}
            {statusToText({ opportunity }) === 'Lost' && (
                <Chip label='Lost' color='chip-lost' sx={chipSx} />
            )}
        </>
    )
}

export default EmailPage;

const contentTypeToFontAwesome = {
    // Documents
    'application/pdf': faFilePdf,
    'application/msword': faFileWord,
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': faFileWord,
    'application/vnd.ms-excel': faFileExcel,
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': faFileExcel,
    'application/vnd.ms-powerpoint': faFilePowerpoint,
    'application/vnd.openxmlformats-officedocument.presentationml.presentation': faFilePowerpoint,
    'text/plain': faFileLines,
    'text/csv': faFileCsv,
    'application/rtf': faFileLines,
  
    // Images
    'image/jpeg': faFileImage,
    'image/png': faFileImage,
    'image/gif': faFileImage,
    'image/webp': faFileImage,
    'image/svg+xml': faFileImage,
  
    // Audio
    'audio/mpeg': faFileAudio,
    'audio/wav': faFileAudio,
    'audio/ogg': faFileAudio,
  
    // Video
    'video/mp4': faFileVideo,
    'video/quicktime': faFileVideo,
    'video/x-msvideo': faFileVideo,
    'video/x-matroska': faFileVideo,
  
    // Archives
    'application/zip': faFileZipper,
    'application/x-rar-compressed': faFileZipper,
    'application/x-7z-compressed': faFileZipper,
    'application/x-tar': faFileZipper,
    'application/gzip': faFileZipper,
  
    // Code
    'text/html': faFileCode,
    'application/javascript': faFileCode,
    'text/javascript': faFileCode,
    'application/json': faFileCode,
    'application/xml': faFileCode,
    'text/css': faFileCode,
  
    // Others
    'application/octet-stream': faFile,
    'application/vnd.google-apps.document': faFileWord,
    'application/vnd.google-apps.spreadsheet': faFileExcel,
    'application/vnd.google-apps.presentation': faFilePowerpoint
  };

const tested = [];
async function notifyOnRateRequestEmails({ conversations, opportunities, notifications, myEmail, org, tokens, accesstoken }) {
    const allRateRequestConversationIds = _.compact(opportunities?.map(o => o.rateRequests?.map(rr => rr.attributes?.conversationId)).flat().filter(id => !!id));
    const rateRequestsEmails = conversations?.filter(c => c.length > 1 && _.some(c, cc => allRateRequestConversationIds.includes(cc.conversationId)));
    const newRequestsEmails = _.compact(rateRequestsEmails?.map(c => {
        const replyMessages = c.filter(cc => cc.toRecipients?.some(r => r.emailAddress?.address === myEmail));
        if (_.isEmpty(replyMessages)) return null;
        const lastReplyMessage = _.maxBy(replyMessages, a => dayjs(a.receivedDateTime).valueOf());
        if (notifications?.find(n => n.type === 'rfq-reply' && n.conversationId === lastReplyMessage.conversationId)) return null;
        if (tested.includes(lastReplyMessage.conversationId)) return null;
        tested.push(lastReplyMessage.conversationId);
        return lastReplyMessage;
    }));

    const fullEmails = await Promise.all(newRequestsEmails.map(m => email.getMessagesForConversation({ org, tokens, conversationId: m.conversationId })));

    await Promise.all(fullEmails.map(async e => {
        const lastMessage = _.maxBy(e, a => dayjs(a.receivedDateTime).valueOf());
        axios.post(`/api/emailconversations/${lastMessage.conversationId}?organizationId=${org.id}&accesstoken=${accesstoken}`, {
            subject: lastMessage.subject,
            body: lastMessage.body.content
        });
    }));
}